/**
 * Splitting a box's fixed price across its lines - BACKEND.md §10A.107, the owner's item E.
 *
 * **This is a deliberate second copy of `BoxFixedPrice.java`, and the duplication is the point.** The
 * server is what *bills*; these clients are what *show*. If the two disagree the customer sees one
 * number at the checkout and is charged another, which is the §10A.80 shape this whole feature had to
 * avoid. So the arithmetic is mirrored exactly, and the unit tests on both sides use **the same worked
 * example** - 750 over parts of 500 / 200 / 150 giving 441 / 177 / 132 - so a divergence fails
 * somewhere rather than reaching a customer.
 *
 * The same bargain `priceLines` already makes for sizes: *"`ServerPricing` does the same from the same
 * column (§10A.33), so the screen and the bill agree."*
 *
 * It lives in `@agos/shared` rather than in `customer-core` because the manager's till builds boxes
 * too, and no module imports another.
 */

/**
 * The box config id a `boxGroupId` belongs to.
 *
 * The group id is built as `${config.id}:${filling}` by `boxGroupId(config, selection)`, so the
 * prefix is the config. A group id with no colon is returned whole rather than rejected: a box that
 * fails to resolve should price as the sum of its parts, which is what it did before this rule
 * existed.
 */
export function boxConfigIdOf(groupId: string | null | undefined): string {
  if (typeof groupId !== 'string' || groupId.trim() === '') return ''
  const colon = groupId.indexOf(':')
  return colon < 0 ? groupId : groupId.slice(0, colon)
}

/**
 * How many cents each weight is owed out of `totalCents`.
 *
 * Proportional to the weights, by largest remainder, with ties going to the earlier line. **Rounding
 * each share independently does not sum back to the total**, and a box whose lines do not add up to
 * its price is a receipt that does not total what it says (§10A.23).
 *
 * All-zero weights split equally, because a proportional split by zero has no answer.
 */
export function allocateBoxPrice(totalCents: number, weights: readonly number[]): number[] {
  if (weights.length === 0) return []
  if (!Number.isFinite(totalCents) || totalCents <= 0) return weights.map(() => 0)

  const total = Math.round(totalCents)
  const weightSum = weights.reduce((sum, weight) => sum + Math.max(0, Math.round(weight)), 0)

  if (weightSum <= 0) {
    const each = Math.floor(total / weights.length)
    const spare = total - each * weights.length
    return weights.map((_, index) => each + (index < spare ? 1 : 0))
  }

  const floors: number[] = []
  const remainders: number[] = []
  for (const weight of weights) {
    const scaled = total * Math.max(0, Math.round(weight))
    floors.push(Math.floor(scaled / weightSum))
    remainders.push(scaled % weightSum)
  }

  let spare = total - floors.reduce((sum, share) => sum + share, 0)
  // Largest remainder first; ties to the earlier line, so the same basket priced twice shows the
  // same numbers. Which line gets the cent matters less than it not moving between renders.
  const order = weights
    .map((_, index) => index)
    .sort((left, right) => remainders[right]! - remainders[left]! || left - right)
  for (const index of order) {
    if (spare <= 0) break
    floors[index] = floors[index]! + 1
    spare -= 1
  }
  return floors
}

export interface BoxPriceable {
  /** Which box this line belongs to, if any. */
  boxGroupId?: string | null
  /** Priced as the catalogue has it, before the box's fixed price is applied. */
  unitCents: number
  /** A gift keeps its zero and is left out of the split. */
  isGift?: boolean
}

/**
 * The unit price each line should show, once a box's fixed price is taken into account.
 *
 * Returns one value per input line, in order, so a caller can map straight back onto whatever shape
 * it holds. Lines with no box, or on a box with no fixed price, come back unchanged - which is every
 * line in the owner's data today, and is asserted rather than assumed.
 *
 * `fixedPrices` is keyed by **config id**, which is what the box configuration stores; the mapping
 * from a line's group id to a config id is `boxConfigIdOf`.
 */
export function withBoxFixedPrices<T extends BoxPriceable>(
  lines: readonly T[],
  fixedPrices: ReadonlyMap<string, number>,
): number[] {
  const unchanged = lines.map((line) => line.unitCents)
  if (fixedPrices.size === 0) return unchanged

  const positionsByGroup = new Map<string, number[]>()
  for (const [index, line] of lines.entries()) {
    const group = line.boxGroupId
    if (typeof group !== 'string' || group === '') continue
    if (!fixedPrices.has(boxConfigIdOf(group))) continue
    const existing = positionsByGroup.get(group)
    if (existing) existing.push(index)
    else positionsByGroup.set(group, [index])
  }
  if (positionsByGroup.size === 0) return unchanged

  const out = [...unchanged]
  for (const [group, positions] of positionsByGroup) {
    const boxCents = fixedPrices.get(boxConfigIdOf(group)) ?? 0
    const payable = positions.filter((index) => lines[index]!.isGift !== true)
    if (payable.length === 0) continue
    const shares = allocateBoxPrice(
      boxCents,
      payable.map((index) => lines[index]!.unitCents),
    )
    for (const [position, index] of payable.entries()) out[index] = shares[position] ?? 0
  }
  return out
}

/**
 * A stored `fixedPrice` in cents, or `null` - §10A.107.
 *
 * The box configuration is a free-form blob written by a settings screen and validated by nothing, so
 * the value may arrive as a number or as a string, with a comma or a point. Anything that is not a
 * positive amount is *not set*: `0`, `null`, `""`, a word somebody typed. The server requires greater
 * than zero for the same reason - in a blob, zero and absent cannot be told apart.
 */
export function boxFixedPriceCentsOf(raw: unknown): number | null {
  const value = typeof raw === 'string' ? Number(raw.replace(',', '.')) : raw
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return null
  return Math.round(value * 100)
}

/**
 * Every configured box's fixed price, by config id, straight off `GET /burger-box-config`.
 *
 * **Takes the raw payload rather than a parsed config**, and that is why it is here: the customer's
 * menu screen and its checkout both need this map, no module imports another, and the checkout has no
 * business knowing the menu module's `BoxConfig` type. An empty map is the common case and is what
 * makes `priceLines` skip the pass entirely.
 */
export function boxFixedPricesOf(payload: unknown): Map<string, number> {
  const prices = new Map<string, number>()
  if (!Array.isArray(payload)) return prices
  for (const raw of payload) {
    if (typeof raw !== 'object' || raw === null) continue
    const row = raw as Record<string, unknown>
    const id = typeof row.id === 'string' ? row.id.trim() : ''
    const cents = boxFixedPriceCentsOf(row.fixedPrice)
    if (id !== '' && cents !== null) prices.set(id, cents)
  }
  return prices
}
